import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ShoppingBag, Loader2, CheckCircle, Lock, Download, Truck, Plus, Trash2 } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import Layout from '../components/Layout';
import SourceChips from '../components/SourceChips';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import { useTranslation } from '../context/TranslationContext';
import { useSanityProducts } from '../hooks/useSanityProducts';
import checkoutText from '../data/checkoutText';
import SHIPPING_COUNTRIES from '../data/countries';
import UpsellOffer from '../components/UpsellOffer';
import { displayBookName, isIndexedBook, bookPlatform } from '../utils/bookGrouping';
// Iframe-native fetch bypass — see /app/frontend/src/lib/nativeFetch.js.
// Both CheckoutPage and the admin OrderEditPanel use the same helper so a
// fetch-wrapper installed by emergent-main.js / PostHog can't consume our
// response bodies before we read them.
import { getNativeFetch, fetchJSON } from '../lib/nativeFetch';

var BACKEND = process.env.REACT_APP_BACKEND_URL;

function _ckPlatform(p) { return p && isIndexedBook(p) ? bookPlatform(p) : null; }
var PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
var STRIPE_PUBLISHABLE_KEY = 'pk_live_3rrTauu6FfQ3sg4MYTmHwOku00GhrQWRaP';
// loadStripe must be called once at module scope (not per render)
var stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// Variant labels stored on cart items contain literal USD amounts like
// "(+$9.99)" or "(Custom Color $99.99)". Re-render those embedded prices in
// the shopper's selected currency so checkout summary lines don't mix USD
// with the customer's local currency (which was confusing customers who saw
// "+$9.99" next to a "CHF 28.27" header and thought they were being charged
// 9.99 CHF — see currency-conversion bug fix 2026-02-23).
function _reformatVariantLabel(label, formatPrice) {
  if (!label || typeof label !== 'string' || typeof formatPrice !== 'function') return label;
  return label.replace(/\+?\$\s?([0-9]+(?:,[0-9]{3})*(?:\.[0-9]+)?)/g, function (m, num) {
    var plus = m.charAt(0) === '+';
    var v = parseFloat(num.replace(/,/g, ''));
    if (!isFinite(v)) return m;
    try { var s = formatPrice(v); return plus ? '+' + s : s; } catch (_e) { return m; }
  });
}

function CheckoutPage() {
  var cart = useCart();
  var langRef = useTranslation();
  var language = langRef.language;
  var formatPrice = langRef.formatPrice;  // USD → shopper's selected currency
  var formatDualPrice = langRef.formatDualPrice;  // "$10.00 USD (approx. €8.65 EUR)"
  var currency = langRef.currency || 'USD';
  var tx = checkoutText[language] || checkoutText.en;
  var navigate = useNavigate();
  var sanityRef = useSanityProducts({});
  var allProducts = sanityRef.products;

  var [submitting, setSubmitting] = useState(false);
  var [errorMessage, setErrorMessage] = useState('');
  var [success, setSuccess] = useState(null);
  // Ref to always read the latest form values inside PayPal callbacks
  // (PayPal SDK captures stale closures otherwise)
  var stateRef = useRef({});
  var paymentRef = useRef(null);

  var [contact, setContact] = useState({ email: '', name: '', phone: '' });
  var [shipping, setShipping] = useState({
    first_name: '', last_name: '', phone: '', street_address: '', extended_address: '',
    locality: '', region: '', postal_code: '', country_code_alpha2: ''
  });
  var [sameAsContact, setSameAsContact] = useState(false);  // checkbox: copy contact name → shipping
  var [shippingInsurance, setShippingInsurance] = useState(false);  // optional $4 add-on for physical orders
  var [noteturnerWarranty, setNoteturnerWarranty] = useState(false);  // optional $15 2-year warranty when NoteTurner is in cart
  var [discountInput, setDiscountInput] = useState('');             // raw text the user types
  var [discountApplied, setDiscountApplied] = useState('');         // active code (validated)
  var [discountError, setDiscountError] = useState('');
  // Active sitewide holiday-discount window (Labor Day, Black Friday, etc.).
  // Server auto-applies 15 % off eligible items (skips instruments + items ≥ $400).
  // If we don't factor it into grandTotal, the server 422's with a Total mismatch.
  var [holidayDiscount, setHolidayDiscount] = useState(null);
  useEffect(function() {
    var API = process.env.REACT_APP_BACKEND_URL;
    fetch(API + '/api/holiday-discount')
      .then(function(r) { return r.ok ? r.json() : { current: null }; })
      .then(function(d) { setHolidayDiscount((d && d.current) || null); })
      .catch(function() { /* banner already surfaces errors; ignore here */ });
  }, []);
  var [quote, setQuote] = useState(null);  // { options, tax_rate, region, category }
  var [selectedMethod, setSelectedMethod] = useState('');  // option id
  var [quoteLoading, setQuoteLoading] = useState(false);

  var requiresShipping = cart.hasPhysical;

  // Fetch shipping quote whenever country changes
  useEffect(function() {
    if (!requiresShipping) {
      setQuote({ options: [], tax_rate: 0, region: '', category: null });
      return;
    }
    // Do NOT call the quote endpoint until the shopper has explicitly picked
    // a country. The previous version fell back to navigator.language which
    // pre-applied US 10% VAT (and a US shipping rate) before the customer
    // chose a destination — confusing EU shoppers who saw a VAT line before
    // selecting their country (user bug report 2026-02-23). Keep the order
    // summary in a "no-quote" state until country_code_alpha2 is set.
    var rawCountry = (shipping.country_code_alpha2 || '').trim().toUpperCase();
    if (!rawCountry || rawCountry.length !== 2) {
      setQuote({ options: [], tax_rate: 0, region: '', category: null });
      setSelectedMethod('');
      return;
    }
    setQuoteLoading(true);
    var items = cart.items.map(function(i) {
      return {
        slug: i.product.slug, name: i.product.name,
        quantity: i.qty, unit_price: i.unitPrice || i.product.price,
        is_digital: !!i.isDigital,
        category: i.product.category || 'accessories',
        weight_lb: i.product.weight_lb || null,
      };
    });
    fetchJSON(BACKEND + '/api/checkout/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        country_code: rawCountry,
        items: items,
        subtotal_usd: cart.subtotal || 0,
      })
    })
      .then(function(rj) {
        var d = rj.data;
        setQuote(d);
        // Reset the selected method if the previously-picked id no longer
        // exists in the new country's option set (e.g. shopper switches
        // from US 'usps' → France whose options are 'standard'/'fedex' —
        // without this reset the Shipping line collapses to '—').
        if (d && d.options && d.options.length > 0) {
          var stillValid = !!selectedMethod && d.options.find(function(o) { return o.id === selectedMethod; });
          if (!stillValid) setSelectedMethod(d.options[0].id);
        }
      })
      .catch(function() { setQuote(null); })
      .finally(function() { setQuoteLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipping.country_code_alpha2, cart.items.length, requiresShipping]);

  var selectedOption = quote && quote.options ? quote.options.find(function(o) { return o.id === selectedMethod; }) : null;
  var shippingCost = selectedOption ? selectedOption.rate_usd : 0;
  var taxRate = quote ? quote.tax_rate : 0;
  // US customers also pay a flat $2 import fee (returned by the shipping quote).
  var flatTax = (quote && quote.flat_tax_usd) ? quote.flat_tax_usd : 0;
  var taxAmount = ((cart.subtotal || 0) + shippingCost) * taxRate + flatTax;
  // $4 shipping-insurance opt-in. Only billed for orders that contain at least
  // one physical item (digital-only orders ignore the flag, matching backend).
  var insuranceAmount = (shippingInsurance && requiresShipping) ? 4.0 : 0;
  // $15 optional 2-year NoteTurner warranty (one replacement per year, customer pays shipping).
  // Only billed when the cart actually contains NoteTurner.
  var hasNoteturner = !!(cart.items && cart.items.find(function(i) { return (i.product && i.product.slug || '').toLowerCase() === 'noteturner'; }));
  var warrantyAmount = (noteturnerWarranty && hasNoteturner) ? 15.0 : 0;
  // 15% off subtotal for the "Thank you" code (case-insensitive). Discount is
  // re-validated server-side at create-intent / capture time.
  // Owner-only test code 'gokugoku' forces grandTotal to exactly $1.
  // Owner-only test code 'free100' forces grandTotal to exactly $0 and
  // skips PayPal/Stripe entirely (see the free-order bypass button below).
  // Auto-computed sitewide holiday discount (matches services/holiday_discounts.py).
  // Skips items in EXCLUDED_CATEGORIES ('digital','instruments','sheet-music'),
  // items priced >= $400, and any item flagged isDigital in the cart itself.
  var holidayDiscountAmount = 0;
  if (holidayDiscount && holidayDiscount.percent_off) {
    var pct = holidayDiscount.percent_off / 100;
    var floor = (holidayDiscount.excludes && holidayDiscount.excludes.price_floor_usd) || 400;
    var excludedCats = (holidayDiscount.excludes && holidayDiscount.excludes.categories) || ['instruments'];
    for (var hi = 0; hi < (cart.items || []).length; hi++) {
      var it = cart.items[hi] || {};
      if (it.isDigital) continue;
      var prod = it.product || {};
      var cat = (prod.category || '').toLowerCase();
      var price = Number(prod.price || 0);
      if (excludedCats.indexOf(cat) !== -1) continue;
      if (price >= floor) continue;
      var unit = Number(it.unitPrice != null ? it.unitPrice : prod.price || 0);
      var qty = Number(it.qty != null ? it.qty : 1);
      holidayDiscountAmount += unit * qty * pct;
    }
    holidayDiscountAmount = Math.round(holidayDiscountAmount * 100) / 100;
  }
  var isGoku = discountApplied && discountApplied.toLowerCase() === 'gokugoku';
  var isFree100 = discountApplied && discountApplied.toLowerCase() === 'free100';
  var grossBeforeDiscount = (cart.subtotal || 0) + shippingCost + taxAmount + insuranceAmount + warrantyAmount;
  var manualDiscountAmount = isGoku
    ? Math.max(0, Math.round((grossBeforeDiscount - 1.00) * 100) / 100)
    : isFree100
      ? Math.round(grossBeforeDiscount * 100) / 100  // 100 % off — total = $0
      : (discountApplied ? Math.round((cart.subtotal || 0) * 0.15 * 100) / 100 : 0);
  var discountAmount = manualDiscountAmount + holidayDiscountAmount;
  var grandTotal = grossBeforeDiscount - discountAmount;

  function tryApplyDiscount() {
    var code = (discountInput || '').trim();
    if (!code) { setDiscountError(''); setDiscountApplied(''); return; }
    var low = code.toLowerCase();
    if (low === 'thank you' || low === 'gokugoku' || low === 'free100') {
      setDiscountApplied(code);
      setDiscountError('');
    } else {
      setDiscountApplied('');
      setDiscountError('Invalid code');
    }
  }
  function removeDiscount() { setDiscountInput(''); setDiscountApplied(''); setDiscountError(''); }

  // Optional add-ons (shipping insurance + NoteTurner warranty). Rendered in
  // TWO places: inside the order summary on desktop, and ABOVE the payment
  // buttons on mobile — on small screens the summary column falls ~900px
  // below the pay buttons, so customers were paying without ever seeing the
  // $4 insurance opt-in (bug report Jun 2026).
  function renderAddOnToggles(suffix) {
    return React.createElement(React.Fragment, null,
      requiresShipping ? React.createElement('label', {
        className: 'flex items-start gap-3 bg-neutral-900/60 border border-white/10 rounded-xl px-4 py-3 mb-4 cursor-pointer hover:bg-neutral-900',
        'data-testid': 'shipping-insurance-toggle' + suffix
      },
        React.createElement('input', {
          type: 'checkbox',
          checked: shippingInsurance,
          onChange: function(e) { setShippingInsurance(e.target.checked); },
          className: 'mt-1 w-4 h-4 accent-[#d4af37] cursor-pointer',
          'data-testid': 'shipping-insurance-checkbox' + suffix
        }),
        React.createElement('div', { className: 'flex-1' },
          React.createElement('div', { className: 'flex justify-between items-baseline' },
            React.createElement('span', { className: 'text-white text-sm font-medium' }, tx.shippingInsurance || 'Shipping insurance'),
            React.createElement('span', { className: 'text-[#d4af37] text-sm font-semibold' }, '+', formatPrice(4.00))
          ),
          React.createElement('p', { className: 'text-neutral-500 text-xs mt-1 leading-snug' },
            tx.shippingInsuranceDescription || 'If your order is lost or arrives damaged, we will send a free replacement — no questions asked.'
          )
        )
      ) : null,
      hasNoteturner ? React.createElement('label', {
        className: 'flex items-start gap-3 bg-neutral-900/60 border border-[#d4af37]/30 rounded-xl px-4 py-3 mb-4 cursor-pointer hover:bg-neutral-900',
        'data-testid': 'noteturner-warranty-toggle' + suffix
      },
        React.createElement('input', {
          type: 'checkbox',
          checked: noteturnerWarranty,
          onChange: function(e) { setNoteturnerWarranty(e.target.checked); },
          className: 'mt-1 w-4 h-4 accent-[#d4af37] cursor-pointer',
          'data-testid': 'noteturner-warranty-checkbox' + suffix
        }),
        React.createElement('div', { className: 'flex-1' },
          React.createElement('div', { className: 'flex justify-between items-baseline' },
            React.createElement('span', { className: 'text-white text-sm font-medium' }, tx.noteturnerWarrantyTitle || 'NoteTurner — 2-Year Replacement Warranty'),
            React.createElement('span', { className: 'text-[#d4af37] text-sm font-semibold' }, '+', formatPrice(15.00))
          ),
          React.createElement('p', { className: 'text-neutral-500 text-xs mt-1 leading-snug' },
            tx.noteturnerWarrantyDescription || 'One replacement per year for two years. Customer pays shipping for the replacement.'
          )
        )
      ) : null
    );
  }

  // Sync latest state into ref BEFORE rendering children so child effects see fresh values.
  // (Updating in a useEffect runs *after* child effects on the same render cycle, causing
  // a stale grandTotal/shippingCost when the Stripe inline section computes its payload.)
  stateRef.current = {
    cart: cart, contact: contact, shipping: shipping, language: language,
    selectedMethod: selectedMethod, selectedOption: selectedOption,
    shippingCost: shippingCost, taxAmount: taxAmount, taxRate: taxRate,
    shippingInsurance: shippingInsurance, insuranceAmount: insuranceAmount,
    noteturnerWarranty: noteturnerWarranty, warrantyAmount: warrantyAmount, hasNoteturner: hasNoteturner,
    discountCode: discountApplied || null, discountAmount: discountAmount,
    grandTotal: grandTotal, requiresShipping: requiresShipping,
  };

  // What's still missing before payment can start (null when ready). Shown as
  // an amber hint inside the payment card so nobody thinks PayPal is broken.
  var validationMsg = validate({ allowPayPalShipping: true });

  // Clear a stale "field required" message as soon as the form becomes valid.
  useEffect(function() {
    if (!validationMsg) setErrorMessage(function(prev) { return prev ? '' : prev; });
  }, [validationMsg]);

  function handleContactChange(e) {
    setContact(function(p) { var n = Object.assign({}, p); n[e.target.name] = e.target.value; return n; });
  }
  function handleShippingChange(e) {
    setShipping(function(p) { var n = Object.assign({}, p); n[e.target.name] = e.target.value; return n; });
  }

  // Auto-copy contact name + phone to shipping when checkbox is checked
  useEffect(function() {
    if (!sameAsContact) return;
    var fullName = (contact.name || '').trim();
    var parts = fullName.split(/\s+/);
    var first = parts[0] || '';
    var last = parts.slice(1).join(' ') || '';
    setShipping(function(p) {
      return Object.assign({}, p, {
        first_name: first,
        last_name: last,
        phone: contact.phone || p.phone,
      });
    });
  }, [sameAsContact, contact.name, contact.phone]);

  // ----- Abandoned-cart capture -----
  // Whenever the user has a valid email + items in the cart, upsert the cart
  // server-side (debounced). A background scanner sends a recovery email if
  // no order is placed within 24h. The PayPal forceReRender array is empty,
  // so this does NOT cause a popup remount.
  useEffect(function() {
    var email = (contact.email || '').trim();
    var isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValid) return;
    if (!cart.items || cart.items.length === 0) return;
    var timer = setTimeout(function() {
      var items = cart.items.map(function(i) {
        return {
          slug: i.product.slug,
          name: i.product.name,
          quantity: i.qty,
          unit_price: i.unitPrice || i.product.price,
          variant: i.variant || (i.product && i.product.variantLabel) || null,
          is_digital: !!i.isDigital,
          image: (i.product && (i.product.image || (i.product.images && i.product.images[0]))) || null,
          category: i.product.category || 'accessories',
        };
      });
      var resumeUrl = '';
      try { resumeUrl = window.location.origin + '/checkout'; } catch (e) {}
      fetch(BACKEND + '/api/checkout/cart/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          language: language,
          currency: 'USD',
          items: items,
          subtotal_usd: cart.subtotal || 0,
          resume_url: resumeUrl,
        }),
      }).catch(function() { /* non-blocking */ });
    }, 1500);
    return function() { clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.email, cart.items.length, cart.subtotal, language]);

  function validate(options) {
    options = options || {};
    var allowPayPalShipping = !!options.allowPayPalShipping;
    if (!contact.email) return tx.email + ' — ' + tx.requiredField;
    if (requiresShipping && !allowPayPalShipping) {
      if (!shipping.country_code_alpha2) return tx.country + ' — ' + tx.requiredField;
      var required = ['first_name', 'last_name', 'phone', 'street_address', 'locality', 'region', 'postal_code'];
      for (var i = 0; i < required.length; i++) {
        if (!shipping[required[i]]) return tx.shipping + ': ' + (tx[required[i]] || required[i]) + ' — ' + tx.requiredField;
      }
      if (!selectedMethod) return tx.shippingMethod + ' — ' + tx.requiredField;
    } else if (requiresShipping && allowPayPalShipping) {
      // For PayPal we let the buyer pick a shipping address INSIDE PayPal —
      // they will be prompted to use that address (with consent) when the
      // popup closes. But we still need a country to compute a shipping
      // estimate, and a shipping method selection. If the form is blank we
      // assume US for the estimate.
      if (!selectedMethod) return tx.shippingMethod + ' — ' + tx.requiredField;
    }
    return null;
  }

  function buildOrderPayload() {
    var s = stateRef.current;
    if (!s || !s.cart) {
      // stateRef not yet populated (first render). Return safe defaults; the
      // StripeInlineSection's useEffect will re-fire once grandTotal stabilizes.
      return {
        customer_email: 'pending@jimendez.com',
        items: [],
        subtotal_usd: 0,
        total_usd: 0.01,
        shipping_cost: 0,
        tax_amount: 0,
        tax_rate: 0,
      };
    }
    var items = s.cart.items.map(function(i) {
      return {
        slug: i.product.slug,
        name: i.product.name,
        quantity: i.qty,
        unit_price: i.unitPrice || i.product.price,
        variant: i.variant || null,
        is_digital: !!i.isDigital,
        image: i.variantImage || i.product.image || (i.product.images && i.product.images[0]) || null,
        category: i.product.category || 'accessories',
        weight_lb: i.product.weight_lb || null,
      };
    });
    return {
      customer_email: s.contact.email,
      customer_name: s.contact.name || null,
      customer_phone: s.contact.phone || s.shipping.phone || null,
      language: s.language,
      items: items,
      subtotal_usd: parseFloat((s.cart.subtotal || 0).toFixed(2)),
      shipping_cost: parseFloat(s.shippingCost.toFixed(2)),
      shipping_method_id: s.selectedMethod || null,
      shipping_method_name: s.selectedOption ? s.selectedOption.name : null,
      tax_amount: parseFloat(s.taxAmount.toFixed(2)),
      tax_rate: s.taxRate,
      shipping_insurance: !!s.shippingInsurance && s.requiresShipping,
      noteturner_warranty: !!s.noteturnerWarranty && !!s.hasNoteturner,
      discount_code: s.discountCode || null,
      total_usd: parseFloat(s.grandTotal.toFixed(2)),
      shipping_address: s.requiresShipping ? Object.assign({}, s.shipping, {
        country_code_alpha2: (s.shipping.country_code_alpha2 || '').toUpperCase()
      }) : null,
    };
  }

  // PayPal Smart Button — createOrder: validates form + calls backend to create PayPal order.
  // NOTE: validation should normally have already happened in onClick (which rejects the popup
  // before it opens). This is a defence-in-depth check for the rare race where state changes
  // between onClick and createOrder.
  async function paypalCreateOrder() {
    setErrorMessage('');
    var validationErr = validate({ allowPayPalShipping: true });
    if (validationErr) {
      setErrorMessage(validationErr);
      return Promise.reject(validationErr);
    }
    var body = buildOrderPayload();
    try {
      var resJ = await fetchJSON(BACKEND + '/api/checkout/paypal/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      var data = resJ.data || {};
      if (!resJ.ok || !data.paypal_order_id) {
        var msg = (data && data.detail) ? data.detail : (tx.paymentFailed || 'Could not create order');
        setErrorMessage(msg);
        return Promise.reject(msg);
      }
      return data.paypal_order_id;
    } catch (e) {
      var em = (e && e.message) || (tx.paymentFailed || 'Could not create order');
      setErrorMessage(em);
      return Promise.reject(em);
    }
  }

  // PayPal Smart Button — onApprove: PayPal popup completed, capture the payment.
  // If PayPal returned a shipping address that differs from (or fills in for)
  // what the customer typed, we ASK them whether to use the PayPal version
  // before submitting the capture. This gives the customer full control over
  // which address the order ships to.
  async function paypalOnApprove(data, actions) {
    setSubmitting(true);
    setErrorMessage('');
    var addressOverride = null;
    try {
      // Fetch PayPal's full order (includes payer email, name, address)
      var ppOrder = null;
      try { ppOrder = actions && actions.order ? await actions.order.get() : null; } catch (e1) { ppOrder = null; }
      var pu = ppOrder && ppOrder.purchase_units && ppOrder.purchase_units[0];
      var ppShip = pu && pu.shipping;
      var ppAddr = ppShip && ppShip.address;
      var ppNameFull = (ppShip && ppShip.name && ppShip.name.full_name) || '';
      var ppPayerEmail = ppOrder && ppOrder.payer && ppOrder.payer.email_address;
      if (ppAddr && (ppAddr.address_line_1 || ppAddr.postal_code)) {
        // Compare to what the customer typed — if anything differs, prompt.
        var curShip = stateRef.current.shipping || {};
        var differs = (
          (curShip.street_address || '') !== (ppAddr.address_line_1 || '') ||
          (curShip.postal_code || '') !== (ppAddr.postal_code || '') ||
          ((curShip.country_code_alpha2 || '').toUpperCase()) !== ((ppAddr.country_code || '').toUpperCase())
        );
        var formMostlyEmpty = !curShip.street_address || !curShip.first_name;
        if (differs || formMostlyEmpty) {
          var first = ppNameFull.split(/\s+/)[0] || '';
          var lastArr = ppNameFull.split(/\s+/);
          var last = lastArr.length > 1 ? lastArr.slice(1).join(' ') : '';
          var lines = [
            (first + ' ' + last).trim(),
            ppAddr.address_line_1 || '',
            (ppAddr.admin_area_2 || '') + (ppAddr.admin_area_1 ? ', ' + ppAddr.admin_area_1 : '') + (ppAddr.postal_code ? ' ' + ppAddr.postal_code : ''),
            (ppAddr.country_code || '').toUpperCase(),
          ].filter(Boolean).join('\n');
          var msg = 'PayPal gave us this shipping address:\n\n' + lines + '\n\nUse this address for your order?\n\nClick OK to use the PayPal address, or Cancel to keep what you entered.';
          var useIt = window.confirm(msg);
          if (useIt) {
            addressOverride = {
              first_name: first || curShip.first_name || '',
              last_name: last || curShip.last_name || '',
              street_address: ppAddr.address_line_1 || curShip.street_address || '',
              extended_address: ppAddr.address_line_2 || curShip.extended_address || '',
              locality: ppAddr.admin_area_2 || curShip.locality || '',
              region: ppAddr.admin_area_1 || curShip.region || '',
              postal_code: ppAddr.postal_code || curShip.postal_code || '',
              country_code_alpha2: (ppAddr.country_code || curShip.country_code_alpha2 || '').toUpperCase(),
              phone: curShip.phone || '',
            };
            // Update the visible form fields too — customer should see what got used
            setShipping(addressOverride);
          }
        }
      }
      // If PayPal gave us an email and the form had none, use it
      var emailOverride = null;
      if (ppPayerEmail && !(stateRef.current.contact && stateRef.current.contact.email)) {
        emailOverride = ppPayerEmail;
        setContact({ email: ppPayerEmail });
      }

      var basePayload = buildOrderPayload();
      if (addressOverride) basePayload.shipping_address = addressOverride;
      if (emailOverride) basePayload.customer_email = emailOverride;

      var body = Object.assign({ paypal_order_id: data.orderID }, basePayload);
      var resJ2 = await fetchJSON(BACKEND + '/api/checkout/paypal/capture-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      var json = resJ2.data || {};
      if (!resJ2.ok || !json.success) {
        throw new Error((json && json.detail) || tx.paymentFailed);
      }
      setSuccess({
        orderNumber: json.order_number,
        email: emailOverride || stateRef.current.contact.email,
        downloadLinks: json.download_links || [],
      });
      cart.clearCart();
    } catch (e) {
      console.error(e);
      setErrorMessage(e.message || tx.paymentFailed || 'Payment failed');
    } finally {
      setSubmitting(false);
    }
  }

  // ----- Stripe Hosted Checkout (Card / Apple Pay / Google Pay) -----
  async function handleStripeCheckout() {
    setErrorMessage('');
    var validationErr = validate();
    if (validationErr) {
      setErrorMessage(validationErr);
      if (paymentRef.current) paymentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      var body = Object.assign({ origin_url: window.location.origin }, buildOrderPayload());
      var resJ3 = await fetchJSON(BACKEND + '/api/checkout/stripe/create-session', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      var data = resJ3.data || {};
      if (!resJ3.ok || !data.url) {
        throw new Error((data && data.detail) || 'Could not start Stripe checkout');
      }
      // Persist session id so we can poll on return even after a hard refresh
      window.localStorage.setItem('jimendez_stripe_session_id', data.session_id);
      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      setErrorMessage(e.message || 'Stripe checkout failed');
      setSubmitting(false);
    }
  }

  // ----- FREE100 test coupon: bypass PayPal/Stripe entirely -----
  // Owner-only. Server hard-rejects anything other than FREE100 on this
  // endpoint, so this can't be abused as a real free-order pipeline.
  async function handleCompleteFreeOrder() {
    setErrorMessage('');
    var validationErr = validate();
    if (validationErr) { setErrorMessage(validationErr); return; }
    setSubmitting(true);
    try {
      var body = buildOrderPayload();
      body.total_usd = 0.0;  // server enforces this too — belt & suspenders
      var res = await fetchJSON(BACKEND + '/api/checkout/complete-free', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      var json = res.data || {};
      if (!res.ok || !json.success) {
        throw new Error((json && json.detail) || 'Free order failed');
      }
      setSuccess({
        orderNumber: json.order_number,
        email: stateRef.current.contact.email,
        downloadLinks: json.download_links || [],
      });
      cart.clearCart();
    } catch (e) {
      console.error(e);
      setErrorMessage(e.message || 'Free order failed');
    } finally {
      setSubmitting(false);
    }
  }

  // ----- Handle return from Stripe hosted (legacy) OR inline 3DS redirect -----
  useEffect(function() {
    var params = new URLSearchParams(window.location.search);
    var sid = params.get('stripe_session_id');
    var cancelled = params.get('stripe_cancelled');
    var inlineIntent = params.get('payment_intent');
    var inlineFlag = params.get('stripe_inline');
    if (cancelled) {
      setErrorMessage('Stripe checkout was cancelled. You can try again or pay with PayPal.');
      window.history.replaceState({}, '', '/checkout');
      return undefined;
    }

    // Inline 3DS redirect return
    if (inlineFlag && inlineIntent) {
      setSubmitting(true);
      var pollIntent = async function(attempts) {
        try {
          var rj = await fetchJSON(BACKEND + '/api/checkout/stripe/intent-status/' + encodeURIComponent(inlineIntent));
          var d = rj.data || {};
          if (d.status === 'succeeded' && d.order_number) {
            setSuccess({ orderNumber: d.order_number, email: contact.email, downloadLinks: [] });
            cart.clearCart();
            window.history.replaceState({}, '', '/checkout');
            return;
          }
          if (attempts < 8) setTimeout(function() { pollIntent(attempts + 1); }, 1500);
          else setErrorMessage('Payment is still processing. Check your email shortly.');
        } catch (e) {
          if (attempts < 8) setTimeout(function() { pollIntent(attempts + 1); }, 2000);
        }
      };
      pollIntent(0);
      return undefined;
    }

    // Legacy hosted checkout return
    if (!sid) return undefined;
    var pollAttempts = 0;
    var maxAttempts = 6;
    var poll = async function() {
      pollAttempts += 1;
      try {
        var rj2 = await fetchJSON(BACKEND + '/api/checkout/stripe/status/' + encodeURIComponent(sid));
        var d = rj2.data || {};
        if (d.payment_status === 'paid' && d.order_number) {
          setSuccess({ orderNumber: d.order_number, email: contact.email, downloadLinks: [] });
          cart.clearCart();
          window.localStorage.removeItem('jimendez_stripe_session_id');
          window.history.replaceState({}, '', '/checkout');
          return;
        }
        if (pollAttempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setErrorMessage('Payment is still processing. Check your email for confirmation, or contact support.');
        }
      } catch (e) {
        if (pollAttempts < maxAttempts) setTimeout(poll, 2500);
      }
    };
    setSubmitting(true);
    poll();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function paypalOnError(err) {
    console.error('PayPal error:', err);
    var raw = String((err && err.message) || err || '');
    // PayPal's SDK sometimes surfaces internal browser/network errors (e.g.,
    // "Body is disturbed or locked", popup closed by 3rd-party script, etc.)
    // that mean nothing to the customer. Show a friendly message unless the
    // error is genuinely informative.
    var isOpaque = /body is disturbed|body locked|body is locked|body stream already read|stream already read|stream is locked|failed to fetch|networkerror|zoid|popup close|window closed/i.test(raw);
    var friendly = (tx.paymentFailed || 'Payment failed. Please try again or use a different card.');
    setErrorMessage(isOpaque ? friendly : (raw || friendly));
    setSubmitting(false);
  }


  // ---- Success state ----
  if (success) {
    return React.createElement(Layout, null,
      React.createElement(SEO, { title: 'Order Confirmed', url: '/checkout', noindex: true }),
      React.createElement('section', { className: 'pt-32 pb-32 bg-black min-h-screen' },
        React.createElement('div', { className: 'max-w-2xl mx-auto px-6 text-center', 'data-testid': 'checkout-success' },
          React.createElement(CheckCircle, { className: 'w-20 h-20 text-[#d4af37] mx-auto mb-6' }),
          React.createElement('h1', { className: 'text-4xl md:text-5xl font-bold text-white mb-4' }, tx.successTitle),
          React.createElement('p', { className: 'text-neutral-400 mb-2' }, tx.successBody),
          React.createElement('p', { className: 'text-white font-semibold mb-6' }, success.email),
          React.createElement('p', { className: 'text-neutral-500 mb-2' }, tx.orderNumber),
          React.createElement('p', { className: 'text-[#d4af37] text-2xl font-mono font-bold mb-10' }, success.orderNumber),
          (success.downloadLinks && success.downloadLinks.length > 0) ?
            React.createElement('div', { className: 'bg-neutral-900 border border-[#d4af37]/30 rounded-2xl p-6 mb-8 text-left' },
              React.createElement('h3', { className: 'text-[#d4af37] font-semibold mb-3 flex items-center gap-2' },
                React.createElement(Download, { className: 'w-5 h-5' }),
                tx.yourDownloads
              ),
              React.createElement('ul', { className: 'space-y-2' },
                success.downloadLinks.map(function(d) {
                  return React.createElement('li', { key: d.url },
                    React.createElement('a', {
                      href: d.url,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      className: 'text-white hover:text-[#d4af37] underline'
                    }, '📥 ', d.name)
                  );
                })
              ),
              React.createElement('p', { className: 'text-neutral-500 text-xs mt-4' },
                'Each file allows 5 downloads within 7 days. You can always get fresh links at ',
                React.createElement('a', { href: '/my-downloads', className: 'text-[#d4af37]' }, 'jimendez.com/my-downloads'),
                '.'
              )
            ) : null,
          React.createElement(SourceChips, { orderNumber: success.orderNumber, email: success.email }),
          // ToneRegistry CTA — show on every successful order
          React.createElement('div', { 'data-testid': 'success-toneregistry-cta', className: 'bg-gradient-to-br from-[#d4af37]/15 to-amber-700/10 border border-[#d4af37]/40 rounded-2xl p-6 mb-8 text-left' },
            React.createElement('h3', { className: 'text-[#d4af37] text-xl font-bold mb-2' }, tx.toneRegistryTitle),
            React.createElement('p', { className: 'text-neutral-300 text-sm mb-4 leading-relaxed' }, tx.toneRegistryBody),
            React.createElement(Link, {
              to: '/toneregistry',
              className: 'inline-block bg-[#d4af37] text-black font-semibold px-6 py-2.5 rounded-full hover:bg-white transition-colors text-sm'
            }, tx.toneRegistryCTA)
          ),
          React.createElement(Link, { to: '/', className: 'inline-block bg-neutral-800 text-white font-semibold px-8 py-3 rounded-full hover:bg-neutral-700 transition-colors' }, tx.backHome)
        )
      )
    );
  }

  // ---- Empty cart ----
  if (cart.items.length === 0) {
    return React.createElement(Layout, null,
      React.createElement(SEO, { title: 'Checkout', url: '/checkout', noindex: true }),
      React.createElement('section', { className: 'pt-32 pb-32 bg-black min-h-screen text-center' },
        React.createElement('div', { className: 'max-w-md mx-auto px-6' },
          React.createElement(ShoppingBag, { className: 'w-16 h-16 text-neutral-700 mx-auto mb-4' }),
          React.createElement('h1', { className: 'text-3xl font-bold text-white mb-3' }, tx.emptyCart),
          React.createElement(Link, { to: '/shop', className: 'inline-block mt-6 bg-[#d4af37] text-black font-semibold px-8 py-3 rounded-full hover:bg-white' }, tx.continueShopping)
        )
      )
    );
  }

  // ---- Checkout form ----
  return React.createElement(Layout, null,
    React.createElement(SEO, { title: 'Checkout', url: '/checkout', noindex: true }),
    React.createElement('section', { className: 'pt-28 pb-20 bg-gradient-to-b from-neutral-950 to-black min-h-screen' },
      React.createElement('div', { className: 'max-w-6xl mx-auto px-6' },
        React.createElement(Link, { to: '/shop', className: 'inline-flex items-center gap-2 text-neutral-400 hover:text-[#d4af37] mb-6 text-sm' },
          React.createElement(ChevronLeft, { className: 'w-4 h-4' }),
          tx.backToCart
        ),
        React.createElement('div', { className: 'flex items-center gap-3 mb-8' },
          React.createElement(Lock, { className: 'w-6 h-6 text-[#d4af37]' }),
          React.createElement('h1', { className: 'text-3xl md:text-4xl font-bold text-white' }, tx.title)
        ),
        React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-3 gap-8' },
          // ---- Left: Form ----
          React.createElement('div', { className: 'lg:col-span-2 space-y-6' },
            // Contact
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6' },
              React.createElement('h2', { className: 'text-lg font-semibold text-white mb-4' }, tx.contactInfo),
              React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-3' },
                React.createElement('input', {
                  type: 'email', name: 'email', value: contact.email,
                  onChange: handleContactChange, placeholder: tx.email + ' *',
                  'data-testid': 'checkout-email-input',
                  className: 'bg-neutral-950 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-neutral-600 focus:border-[#d4af37] focus:outline-none'
                }),
                React.createElement('input', {
                  type: 'text', name: 'name', value: contact.name,
                  onChange: handleContactChange, placeholder: tx.fullName,
                  'data-testid': 'checkout-name-input',
                  className: 'bg-neutral-950 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-neutral-600 focus:border-[#d4af37] focus:outline-none'
                }),
                React.createElement('input', {
                  type: 'tel', name: 'phone', value: contact.phone,
                  onChange: handleContactChange, placeholder: tx.phone,
                  'data-testid': 'checkout-phone-input',
                  className: 'bg-neutral-950 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-neutral-600 focus:border-[#d4af37] focus:outline-none md:col-span-2'
                })
              ),
              // "Same as contact" checkbox — only show if physical shipping needed
              requiresShipping ? React.createElement('label', {
                className: 'flex items-center gap-2 mt-4 cursor-pointer text-neutral-300 text-sm select-none',
                'data-testid': 'same-as-contact-label'
              },
                React.createElement('input', {
                  type: 'checkbox',
                  checked: sameAsContact,
                  onChange: function(e) { setSameAsContact(e.target.checked); },
                  'data-testid': 'same-as-contact-checkbox',
                  className: 'w-4 h-4 accent-[#d4af37] cursor-pointer'
                }),
                tx.sameAsContact
              ) : null
            ),
            // Shipping (only if physical items)
            requiresShipping ? React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6' },
              React.createElement('h2', { className: 'text-lg font-semibold text-white mb-2 flex items-center gap-2' },
                React.createElement(Truck, { className: 'w-5 h-5 text-[#d4af37]' }), tx.shipping),
              React.createElement('p', { 'data-testid': 'shipping-disclaimer', className: 'text-neutral-400 text-xs mb-4 bg-neutral-950 border border-[#d4af37]/20 rounded-lg p-3' }, tx.shippingNote),
              React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-3' },
                React.createElement('select', {
                  name: 'country_code_alpha2', value: shipping.country_code_alpha2,
                  onChange: handleShippingChange, 'data-testid': 'shipping-country',
                  className: shippingInputClass() + ' md:col-span-2'
                },
                  React.createElement('option', { value: '' }, '— ' + tx.chooseCountry + ' * —'),
                  SHIPPING_COUNTRIES.map(function(c) {
                    return React.createElement('option', { key: c.code, value: c.code }, c.name);
                  })
                ),
                React.createElement('input', { name: 'first_name', value: shipping.first_name, onChange: handleShippingChange, placeholder: tx.firstName + ' *', 'data-testid': 'shipping-firstname', className: shippingInputClass() }),
                React.createElement('input', { name: 'last_name', value: shipping.last_name, onChange: handleShippingChange, placeholder: tx.lastName + ' *', 'data-testid': 'shipping-lastname', className: shippingInputClass() }),
                React.createElement('input', { name: 'phone', type: 'tel', value: shipping.phone, onChange: handleShippingChange, placeholder: tx.phone + ' *', 'data-testid': 'shipping-phone', className: shippingInputClass() + ' md:col-span-2' }),
                React.createElement('input', { name: 'street_address', value: shipping.street_address, onChange: handleShippingChange, placeholder: tx.address + ' *', 'data-testid': 'shipping-address', className: shippingInputClass() + ' md:col-span-2' }),
                React.createElement('input', { name: 'extended_address', value: shipping.extended_address, onChange: handleShippingChange, placeholder: tx.apartment, 'data-testid': 'shipping-apt', className: shippingInputClass() + ' md:col-span-2' }),
                React.createElement('input', { name: 'locality', value: shipping.locality, onChange: handleShippingChange, placeholder: tx.city + ' *', 'data-testid': 'shipping-city', className: shippingInputClass() }),
                React.createElement('input', { name: 'region', value: shipping.region, onChange: handleShippingChange, placeholder: tx.state + ' *', 'data-testid': 'shipping-state', className: shippingInputClass() }),
                React.createElement('input', { name: 'postal_code', value: shipping.postal_code, onChange: handleShippingChange, placeholder: tx.postal + ' *', 'data-testid': 'shipping-postal', className: shippingInputClass() + ' md:col-span-2' })
              ),
              // Shipping method picker
              shipping.country_code_alpha2 ? React.createElement('div', { className: 'mt-5', 'data-testid': 'shipping-methods' },
                React.createElement('h3', { className: 'text-white text-sm font-semibold mb-2' }, tx.shippingMethod),
                quoteLoading ? React.createElement('p', { className: 'text-neutral-500 text-sm' }, '…')
                  : (quote && quote.options && quote.options.length > 0 ?
                    React.createElement('div', { className: 'space-y-2' },
                      quote.options.map(function(opt) {
                        return React.createElement('label', {
                          key: opt.id,
                          'data-testid': 'shipping-option-' + opt.id,
                          className: 'flex items-center justify-between gap-3 p-3 rounded-lg cursor-pointer border ' + (selectedMethod === opt.id ? 'border-[#d4af37] bg-[#d4af37]/10' : 'border-white/10 bg-neutral-950 hover:border-white/30')
                        },
                          React.createElement('div', { className: 'flex items-center gap-3' },
                            React.createElement('input', { type: 'radio', name: 'ship_method', checked: selectedMethod === opt.id, onChange: function() { setSelectedMethod(opt.id); }, className: 'accent-[#d4af37]' }),
                            React.createElement('div', null,
                              React.createElement('p', { className: 'text-white text-sm font-medium' }, opt.name),
                              React.createElement('p', { className: 'text-neutral-500 text-xs' }, opt.eta)
                            )
                          ),
                          React.createElement('p', { className: 'text-[#d4af37] font-semibold whitespace-nowrap' }, formatPrice(opt.rate_usd))
                        );
                      })
                    )
                    : React.createElement('p', { className: 'text-neutral-500 text-sm' }, tx.selectCountryFirst)
                  )
              ) : null
            ) : React.createElement('div', { className: 'bg-neutral-900/50 border border-[#d4af37]/30 rounded-2xl p-4 text-center text-[#d4af37] text-sm' }, tx.digitalOnly),
            // Mobile-only add-ons + running total, placed ABOVE the payment
            // buttons (on phones the order-summary column renders far below
            // them, so the $4 insurance opt-in was never seen — Jun 2026 fix).
            (requiresShipping || hasNoteturner) ? React.createElement('div', {
              className: 'lg:hidden bg-neutral-900 border border-white/10 rounded-2xl p-6',
              'data-testid': 'mobile-addons-section'
            },
              React.createElement('h2', { className: 'text-base font-semibold text-white mb-4' }, tx.addOnsTitle || 'Add to your order'),
              renderAddOnToggles('-mobile'),
              React.createElement('div', { className: 'flex justify-between text-white text-base font-bold border-t border-white/10 pt-4', 'data-testid': 'mobile-order-total' },
                React.createElement('span', null, tx.total),
                React.createElement('span', { className: 'text-[#d4af37] text-right' }, formatDualPrice(grandTotal))
              )
            ) : null,
            // Payment (PayPal Smart Buttons)
            React.createElement('div', { ref: paymentRef, className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6' },
              React.createElement('h2', { className: 'text-lg font-semibold text-white mb-2' }, tx.payment),
              React.createElement('p', { className: 'text-neutral-500 text-sm mb-4' }, tx.paymentNote),
              // Inline feedback right where the customer clicks. Previously the
              // only error box lived BELOW the payment card and the page also
              // scrolled to the top on a rejected click — so an incomplete form
              // looked exactly like "PayPal is broken, nothing happens".
              errorMessage ? React.createElement('div', {
                'data-testid': 'payment-error-inline',
                className: 'mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-300 text-sm'
              }, errorMessage)
                : (validationMsg ? React.createElement('div', {
                    'data-testid': 'payment-incomplete-hint',
                    className: 'mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-amber-200 text-sm'
                  },
                    React.createElement('span', { className: 'font-semibold' }, tx.completeFormFirst || 'Complete the form above to pay'),
                    React.createElement('span', null, ' — ', validationMsg)
                  ) : null),
              isFree100 ? (
                // FREE100 bypass — single button, no PayPal / Stripe.
                React.createElement('div', null,
                  React.createElement('div', { className: 'bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-4' },
                    React.createElement('p', { className: 'text-emerald-300 text-sm font-semibold mb-1' }, '\ud83c\udf81 FREE100 test order'),
                    React.createElement('p', { className: 'text-emerald-200/70 text-xs' }, 'This will create a real \u201cpaid\u201d order at $0, send the confirmation email, and skip PayPal / Stripe entirely. Owner-only.')
                  ),
                  React.createElement('button', {
                    type: 'button',
                    onClick: handleCompleteFreeOrder,
                    disabled: submitting || cart.items.length === 0,
                    'data-testid': 'free100-complete-button',
                    className: 'w-full flex items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-400 text-black rounded-full px-6 py-3.5 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                  },
                    submitting ? 'Placing free order\u2026' : 'Complete free order (0.00)'
                  )
                )
              ) : React.createElement('div', null,
                React.createElement('div', { 'data-testid': 'paypal-buttons-container', className: 'min-h-[140px]' },
                PAYPAL_CLIENT_ID ? React.createElement(PayPalButtons, {
                  style: { layout: 'vertical', color: 'gold', shape: 'pill', label: 'paypal', height: 48 },
                  disabled: submitting || cart.items.length === 0,
                  // IMPORTANT: do NOT include rapidly-changing form fields (email, address,
                  // typing) in forceReRender — the PayPal SDK calls `zoid.destroy` on every
                  // change which kills the button mid-mount ("zoid destroyed all components").
                  // `paypalCreateOrder` builds its payload at click time from `stateRef`, so
                  // a stale closure isn't a concern. Empty deps = render once and stay mounted.
                  forceReRender: [],
                  // onClick runs BEFORE the popup opens. Validate here and call
                  // actions.reject() if the form isn't complete — that way the empty
                  // PayPal popup never spawns and Safari does not show
                  // "Can't open this link" when our createOrder later rejects.
                  onClick: function(data, actions) {
                    var err = validate({ allowPayPalShipping: true });
                    if (err) {
                      setErrorMessage(err);
                      // Keep the customer where they clicked so they actually
                      // SEE why the PayPal window didn't open (scrolling to the
                      // top used to hide the message off-screen).
                      if (paymentRef.current) paymentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      return actions.reject();
                    }
                    setErrorMessage('');
                    return actions.resolve();
                  },
                  createOrder: paypalCreateOrder,
                  onApprove: paypalOnApprove,
                  onError: paypalOnError,
                  onCancel: function() { setSubmitting(false); }
                }) : React.createElement('p', { className: 'text-red-400 text-sm' }, 'PayPal not configured')
              ),
              // Pop-up blockers are the #1 reason the PayPal window "doesn't open".
              React.createElement('p', { className: 'text-center text-xs text-neutral-500 mt-2', 'data-testid': 'paypal-popup-hint' },
                tx.paypalPopupHint || 'If the PayPal window doesn’t open, allow pop-ups for this site (or tap “Click to Continue”) and try again.'
              ),
              // ---- OR divider ----
              React.createElement('div', { className: 'flex items-center gap-3 my-4' },
                React.createElement('div', { className: 'flex-1 h-px bg-white/10' }),
                React.createElement('span', { className: 'text-neutral-500 text-xs uppercase tracking-wider' }, 'or'),
                React.createElement('div', { className: 'flex-1 h-px bg-white/10' })
              ),
              // ---- Stripe Hosted Checkout (Card · Apple Pay · Google Pay) ----
              // The inline Express Checkout Element failed at Apple Pay confirm
              // (likely due to the restricted backend key — `rk_live_*` may not
              // have the right scope for Apple Pay token confirmation). The
              // hosted page is undercharge-proof (amount locked server-side at
              // session creation) and renders Apple/Google Pay automatically
              // when the customer's device supports them.
              React.createElement('button', {
                type: 'button',
                onClick: handleStripeCheckout,
                disabled: submitting || cart.items.length === 0,
                'data-testid': 'stripe-checkout-button',
                className: 'w-full flex items-center justify-center gap-3 bg-black text-white border border-white/20 hover:border-white/40 hover:bg-neutral-900 rounded-full px-6 py-3.5 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed',
              },
                React.createElement(Lock, { className: 'w-4 h-4' }),
                'Pay with Card · Apple Pay · Google Pay'
              ),
              React.createElement('p', { className: 'text-center text-xs text-neutral-500 mt-2' },
                'Secure checkout powered by Stripe'
              ),
              React.createElement('p', { className: 'text-center text-xs text-neutral-600 mt-1' },
                'International card? If your card doesn\u2019t support USD, please use PayPal above.'
              )
              )  // close FREE100 conditional else div
            ),
            // Bottom error box kept only for real payment failures (validation
            // messages now render inside the payment card, always in view).
            (errorMessage && !validationMsg) ? React.createElement('div', { 'data-testid': 'checkout-error', className: 'bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300' }, errorMessage) : null
          ),
          // ---- Right: Order Summary ----
          React.createElement('div', { className: 'lg:col-span-1' },
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6 sticky top-28' },
              React.createElement('h2', { className: 'text-lg font-semibold text-white mb-4' }, tx.orderSummary),
              React.createElement('ul', { className: 'space-y-3 mb-4 border-b border-white/10 pb-4' },
                cart.items.map(function(item) {
                  var price = (item.unitPrice || item.product.price);
                  return React.createElement('li', { key: item.key, className: 'flex gap-3 items-start' },
                    React.createElement('div', { className: 'relative' },
                      React.createElement('img', { src: item.variantImage || item.product.image || (item.product.images && item.product.images[0]) || '', alt: item.product.name, className: 'w-14 h-14 rounded-lg object-cover' })
                    ),
                    React.createElement('div', { className: 'flex-1 min-w-0' },
                      React.createElement('p', { className: 'text-sm font-medium truncate ' + (_ckPlatform(item.product) === 'mobilesheets' ? 'text-emerald-400' : _ckPlatform(item.product) === 'forscore' ? 'text-sky-400' : 'text-white') },
                        _ckPlatform(item.product) ? displayBookName(item.product) : item.product.name
                      ),
                      _ckPlatform(item.product) ? React.createElement('p', {
                        className: 'text-[10px] font-bold uppercase tracking-wider ' + (_ckPlatform(item.product) === 'mobilesheets' ? 'text-emerald-400/80' : 'text-sky-400/80')
                      }, '(', _ckPlatform(item.product) === 'mobilesheets' ? 'MobileSheets' : 'forScore', ')') : null,
                      item.variant ? React.createElement('p', { className: 'text-neutral-500 text-xs truncate' }, _reformatVariantLabel(item.variant, formatPrice)) : null,
                      // Quantity controls
                      React.createElement('div', { className: 'flex items-center gap-2 mt-1.5' },
                        React.createElement('button', {
                          type: 'button',
                          onClick: function() { cart.updateQty(item.key, item.qty - 1); },
                          'data-testid': 'summary-qty-minus-' + item.product.slug,
                          'aria-label': 'Decrease quantity',
                          className: 'w-6 h-6 rounded-md bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-bold flex items-center justify-center transition-colors'
                        }, '−'),
                        React.createElement('span', { className: 'text-white text-sm font-semibold min-w-[20px] text-center', 'data-testid': 'summary-qty-' + item.product.slug }, item.qty),
                        React.createElement('button', {
                          type: 'button',
                          onClick: function() { cart.updateQty(item.key, item.qty + 1); },
                          'data-testid': 'summary-qty-plus-' + item.product.slug,
                          'aria-label': 'Increase quantity',
                          className: 'w-6 h-6 rounded-md bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-bold flex items-center justify-center transition-colors'
                        }, '+'),
                        React.createElement('button', {
                          type: 'button',
                          onClick: function() { cart.removeItem(item.key); },
                          'data-testid': 'summary-remove-' + item.product.slug,
                          'aria-label': 'Remove item',
                          title: 'Remove',
                          className: 'ml-1 w-6 h-6 rounded-md text-neutral-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors'
                        }, React.createElement(Trash2, { className: 'w-3.5 h-3.5' }))
                      )
                    ),
                    React.createElement('p', { className: 'text-white text-sm font-semibold whitespace-nowrap pt-1' }, formatPrice(price * item.qty))
                  );
                })
              ),
              // ----- Cross-sell upsell -----
              React.createElement(UpsellOffer, { cartItems: cart.items, allProducts: allProducts, addToCart: cart.addItem, tx: tx, variant: 'compact' }),
              React.createElement('div', { className: 'space-y-2 text-sm mb-4' },
                React.createElement('div', { className: 'flex justify-between text-neutral-400 gap-3', 'data-testid': 'order-line-subtotal' },
                  React.createElement('span', { className: 'flex-shrink-0' }, tx.subtotal),
                  React.createElement('span', { className: 'text-right' }, formatDualPrice(cart.subtotal || 0))
                ),
                React.createElement('div', { className: 'flex justify-between text-neutral-400 gap-3', 'data-testid': 'order-line-shipping' },
                  React.createElement('span', { className: 'flex-shrink-0' }, tx.shippingCost, selectedOption ? ' (' + selectedOption.name.split(' — ')[0] + ')' : ''),
                  React.createElement('span', { className: 'text-right' }, requiresShipping && selectedOption ? formatDualPrice(shippingCost) : (requiresShipping ? '—' : '—'))
                ),
                taxAmount > 0 ? React.createElement('div', { className: 'flex justify-between text-neutral-400' },
                  // Use the dynamic tax_rate from the live shipping quote.
                  // Older translations had "VAT (16%)" hardcoded which was
                  // wrong for the US (10%) and misleading for tax-free
                  // countries. We swap the static "(16%)" suffix with the
                  // actual percentage; flat import-tax stays as-is.
                  React.createElement('span', null,
                    (flatTax > 0 && taxRate === 0)
                      ? (tx.importTax || 'Import Tax')
                      : (function () {
                          var base = tx.tax || 'Tax';
                          var dynamicPct = Math.round((taxRate || 0) * 100);
                          // Replace any existing "(NN%)" suffix in the
                          // localized label with the live rate, e.g.
                          // "VAT (16%)" → "VAT (10%)" for a US cart.
                          if (dynamicPct > 0) {
                            if (/\(\d+%\)/.test(base)) {
                              return base.replace(/\(\d+%\)/, '(' + dynamicPct + '%)');
                            }
                            return base + ' (' + dynamicPct + '%)';
                          }
                          return base.replace(/\s*\(\d+%\)/, '');
                        })()
                  ),
                  React.createElement('span', null, formatDualPrice(taxAmount))
                ) : null,
                insuranceAmount > 0 ? React.createElement('div', { className: 'flex justify-between text-neutral-400', 'data-testid': 'insurance-line' },
                  React.createElement('span', null, tx.shippingInsurance || 'Shipping insurance'),
                  React.createElement('span', null, formatDualPrice(insuranceAmount))
                ) : null,
                warrantyAmount > 0 ? React.createElement('div', { className: 'flex justify-between text-neutral-400', 'data-testid': 'warranty-line' },
                  React.createElement('span', null, tx.noteturnerWarrantyShort || 'NoteTurner 2-Year Warranty'),
                  React.createElement('span', null, formatDualPrice(warrantyAmount))
                ) : null,
                holidayDiscountAmount > 0 ? React.createElement('div', { className: 'flex justify-between text-[#d4af37]', 'data-testid': 'holiday-discount-line' },
                  React.createElement('span', null, '🎉 ', (holidayDiscount && holidayDiscount.name) || 'Holiday', ' (', holidayDiscount && holidayDiscount.percent_off, '% off)'),
                  React.createElement('span', null, '-', formatDualPrice(holidayDiscountAmount))
                ) : null,
                manualDiscountAmount > 0 ? React.createElement('div', { className: 'flex justify-between text-[#d4af37]', 'data-testid': 'discount-line' },
                  React.createElement('span', null, '🎟 ', tx.discount || 'Discount', ' (', discountApplied, ')'),
                  React.createElement('span', null, '-', formatDualPrice(manualDiscountAmount))
                ) : null
              ),
              // Discount code input
              React.createElement('div', { className: 'mb-4', 'data-testid': 'discount-code-section' },
                discountApplied ? (
                  React.createElement('div', { className: 'flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3' },
                    React.createElement('div', null,
                      React.createElement('div', { className: 'text-green-300 text-sm font-semibold' }, '\u2705 ', tx.discountApplied || 'Discount applied', ' (', isFree100 ? '100% off' : isGoku ? 'test — $1' : '15% off', ')'),
                      React.createElement('div', { className: 'text-neutral-400 text-xs mt-0.5' }, tx.discountCode || 'Code', ': ', discountApplied)
                    ),
                    React.createElement('button', { type: 'button', onClick: removeDiscount, 'data-testid': 'discount-remove-btn', className: 'text-neutral-500 hover:text-red-400 text-xs' }, '✕ ', tx.remove || 'Remove')
                  )
                ) : (
                  React.createElement('div', { className: 'flex gap-2' },
                    React.createElement('input', {
                      type: 'text', value: discountInput,
                      onChange: function(e) { setDiscountInput(e.target.value); setDiscountError(''); },
                      onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); tryApplyDiscount(); } },
                      placeholder: tx.discountPlaceholder || 'Discount code',
                      'data-testid': 'discount-code-input',
                      className: 'flex-1 bg-neutral-900 border border-white/10 text-white text-sm rounded-xl px-4 py-2 focus:outline-none focus:border-[#d4af37]'
                    }),
                    React.createElement('button', {
                      type: 'button', onClick: tryApplyDiscount,
                      'data-testid': 'discount-apply-btn',
                      className: 'bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-semibold px-4 rounded-xl'
                    }, tx.apply || 'Apply')
                  )
                ),
                discountError ? React.createElement('p', { className: 'text-red-400 text-xs mt-1', 'data-testid': 'discount-error' }, discountError) : null
              ),
              // Add-on toggles (desktop only — the mobile copy renders above the
              // payment buttons so phone buyers actually see them).
              React.createElement('div', { className: 'hidden lg:block' }, renderAddOnToggles('')),
              React.createElement('div', { className: 'flex justify-between text-white text-lg font-bold border-t border-white/10 pt-4 mb-2', 'data-testid': 'order-line-total' },
                React.createElement('span', { className: 'flex-shrink-0' }, tx.total),
                React.createElement('span', { className: 'text-[#d4af37] text-right' }, formatDualPrice(grandTotal))
              ),
              // Make the actual-currency-of-charge crystal clear. Even though
              // the Total above already shows "$X.XX USD (approx. local …)",
              // we render an explicit "💳 Charged in USD" badge below it
              // because customers asked for unambiguous messaging.
              React.createElement('p', {
                className: 'text-[11px] text-neutral-500 mb-3 italic',
                'data-testid': 'currency-charge-note'
              }, currency === 'USD'
                ? '💳 Your card will be charged in USD.'
                : '💳 Your card will be charged in USD (' + currency + ' figures shown above are an approximate display conversion; your bank applies its real-time FX rate).'
              ),
              // Country-fees disclaimer — reminds the customer that customs / VAT / import duties
              // dictated by their local laws are their responsibility (not part of this checkout).
              React.createElement('p', { 'data-testid': 'country-fees-disclaimer', className: 'text-neutral-500 text-[11px] leading-snug mb-4 italic' },
                tx.countryFeesDisclaimer || 'You may be responsible for additional customs duties, import taxes, or other fees dictated by your country\'s laws on delivery. Please check your country\'s import rules before purchasing.'
              ),
              submitting ? React.createElement('div', {
                'data-testid': 'checkout-processing',
                className: 'w-full flex items-center justify-center gap-2 bg-[#d4af37]/30 text-black font-semibold py-4 rounded-full'
              },
                React.createElement(Loader2, { className: 'w-5 h-5 animate-spin' }),
                tx.processing
              ) : React.createElement('div', { className: 'bg-neutral-800/40 border border-[#d4af37]/30 rounded-xl px-4 py-3 text-center' },
                React.createElement('p', { className: 'text-neutral-300 text-sm' }, '👈 Use the PayPal buttons to pay')
              ),
              React.createElement('p', { className: 'text-neutral-600 text-xs text-center mt-3 flex items-center justify-center gap-1' },
                React.createElement(Lock, { className: 'w-3 h-3' }),
                'Secure checkout • PayPal'
              )
            )
          )
        )
      )
    )
  );
}

function shippingInputClass() {
  return 'bg-neutral-950 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-neutral-600 focus:border-[#d4af37] focus:outline-none';
}

/* UpsellOffer now lives in /components/UpsellOffer.js (shared with CartDrawer) */


// ===== Inline Stripe Express Checkout + Card form =====
// Lazy-creates a PaymentIntent only when the user has filled enough of the form
// to pass validation, then mounts <Elements> with the client_secret.
function StripeInlineSection(props) {
  var clientSecretState = useState(null);
  var clientSecret = clientSecretState[0];
  var setClientSecret = clientSecretState[1];
  var intentIdState = useState(null);
  var intentId = intentIdState[0];
  var setIntentId = intentIdState[1];
  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  // Server-confirmed amount on the intent (in USD). When this changes (e.g.,
  // discount applied, shipping picked) we tell Stripe Elements to refresh.
  var serverAmountState = useState(null);
  var serverAmount = serverAmountState[0];
  var setServerAmount = serverAmountState[1];

  // Recreate PaymentIntent as soon as the cart has items. Apple Pay / Google Pay
  // buttons need a client_secret to render, so we deliberately mount early using
  // subtotal-only when the form isn't filled — then re-create with the full
  // amount as the customer fills email / country / shipping method. The server
  // re-verifies the total at confirm time, so this is safe.
  useEffect(function() {
    if (!stripePromise || props.cartCount === 0) return undefined;
    if (!props.grandTotal || props.grandTotal <= 0) return undefined;
    var body = props.buildOrderPayload();
    if (!body || !body.items || body.items.length === 0) return undefined;
    // Backend now allows null email/address for the initial mount.
    if (!body.customer_email || body.customer_email === 'pending@jimendez.com') {
      body.customer_email = null;
    }
    // If the shipping address isn't filled (country missing), drop it.
    // Backend's ShippingAddress has min_length=2 on country, so we must send null
    // rather than an object with empty fields.
    if (body.shipping_address && (!body.shipping_address.country_code_alpha2 ||
                                   body.shipping_address.country_code_alpha2.length !== 2)) {
      body.shipping_address = null;
      body.shipping_method_id = null;
      body.shipping_method_name = null;
      body.shipping_cost = 0;
      body.tax_amount = 0;
      body.tax_rate = 0;
      body.total_usd = body.subtotal_usd;
    }
    var cancelled = false;
    setLoading(true);
    // Pass the previous intent_id so the backend updates it in place instead
    // of creating a new one — keeps the Stripe dashboard clean.
    if (intentId) body.existing_intent_id = intentId;
    var timer = setTimeout(function() {
      fetchJSON(BACKEND + '/api/checkout/stripe/create-intent', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      }).then(function(rj) {
        var d = rj.data || {};
        if (cancelled) return;
        if (d && d.client_secret) {
          // Track the latest server-confirmed amount so the child Elements
          // tree can call `elements.fetchUpdates()` to refresh the displayed
          // amount in the Apple Pay / Google Pay wallet sheet. Without this,
          // Stripe shows the stale amount that was on the intent at mount.
          setServerAmount(d.amount);
          // Only update React state if the intent ACTUALLY changed (otherwise
          // changing `options.clientSecret` would remount the Elements tree).
          if (d.intent_id !== intentId) {
            setClientSecret(d.client_secret);
            setIntentId(d.intent_id);
          }
        } else {
          setClientSecret(null);
        }
      }).catch(function() { /* silent — user can use PayPal */ })
        .finally(function() { if (!cancelled) setLoading(false); });
    }, 500);
    return function() { cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.grandTotal, props.cartCount, props.contactEmail, props.shippingCountry, props.selectedMethod, props.requiresShipping]);

  if (!stripePromise) return null;
  if (props.cartCount === 0) return null;

  if (loading && !clientSecret) {
    return React.createElement('div', { className: 'min-h-[140px] flex items-center justify-center text-neutral-500 text-sm gap-2' },
      React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }),
      'Loading Apple Pay / Google Pay / Card…'
    );
  }
  if (!clientSecret) {
    return React.createElement('p', { className: 'text-neutral-600 text-sm text-center py-4' }, 'Card / Apple Pay / Google Pay will appear here after you fill the form above.');
  }

  var options = {
    clientSecret: clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#d4af37',
        colorBackground: '#0a0a0a',
        colorText: '#ffffff',
        colorTextSecondary: '#9ca3af',
        colorTextPlaceholder: '#6b7280',
        colorDanger: '#ef4444',
        borderRadius: '12px',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        spacingUnit: '4px',
      },
      rules: {
        '.Input': { borderColor: 'rgba(255,255,255,0.1)' },
        '.Input:focus': { borderColor: '#d4af37' },
      }
    },
  };

  return React.createElement(Elements, { stripe: stripePromise, options: options },
    React.createElement(StripePaymentForm, {
      intentId: intentId,
      serverAmount: serverAmount,
      validate: props.validate,
      onSuccess: props.onSuccess,
      onError: props.onError,
      stateRef: props.stateRef,
    })
  );
}

function StripePaymentForm(props) {
  var stripe = useStripe();
  var elements = useElements();
  var processingState = useState(false);
  var processing = processingState[0];
  var setProcessing = processingState[1];
  var msgState = useState('');
  var msg = msgState[0];
  var setMsg = msgState[1];

  // CRITICAL: when the server updates the PaymentIntent's amount (discount
  // applied, shipping changed), the Stripe Elements tree caches the previous
  // amount. We must call `elements.fetchUpdates()` so the Apple Pay / Google
  // Pay wallet sheet opens with the new amount instead of the stale one.
  // Without this, applying a discount AFTER the Stripe section mounted shows
  // the OLD total in the wallet.
  useEffect(function() {
    if (!elements) return undefined;
    if (props.serverAmount == null) return undefined;
    var cancelled = false;
    elements.fetchUpdates().catch(function(err) {
      if (!cancelled) console.warn('[stripe] fetchUpdates failed:', err);
    });
    return function() { cancelled = true; };
  }, [elements, props.serverAmount]);

  function validateBeforePay() {
    var err = props.validate ? props.validate() : null;
    if (err) {
      props.onError(err);
      return false;
    }
    return true;
  }

  // Express Checkout Element confirm
  async function onExpressConfirm(event) {
    if (!stripe || !elements) return;
    if (!validateBeforePay()) {
      // Cannot complete the Apple/Google Pay sheet — abort
      return;
    }
    setProcessing(true); setMsg('');
    try {
      var sub = await elements.submit();
      if (sub.error) throw new Error(sub.error.message || 'Validation failed');
      var ret = await stripe.confirmPayment({
        elements: elements,
        clientSecret: undefined,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.origin + '/checkout?stripe_inline=1' },
      });
      if (ret.error) throw new Error(ret.error.message || 'Payment failed');
      // No error → succeeded synchronously (no 3DS). Poll backend to finalize.
      await finalize();
    } catch (e) {
      setMsg(e.message || 'Payment could not be completed');
      props.onError(e.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  }

  async function onCardPay() {
    if (!stripe || !elements) return;
    if (!validateBeforePay()) return;
    setProcessing(true); setMsg('');
    try {
      var sub = await elements.submit();
      if (sub.error) throw new Error(sub.error.message || 'Validation failed');
      var ret = await stripe.confirmPayment({
        elements: elements,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.origin + '/checkout?stripe_inline=1' },
      });
      if (ret.error) throw new Error(ret.error.message || 'Payment failed');
      await finalize();
    } catch (e) {
      setMsg(e.message || 'Payment could not be completed');
      props.onError(e.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  }

  async function finalize() {
    // Poll the intent status until succeeded (usually returns immediately)
    var attempts = 0;
    while (attempts < 8) {
      var rj3 = await fetchJSON(BACKEND + '/api/checkout/stripe/intent-status/' + encodeURIComponent(props.intentId));
      var d = rj3.data || {};
      if (d.status === 'succeeded' && d.order_number) {
        var email = (props.stateRef.current && props.stateRef.current.contact && props.stateRef.current.contact.email) || '';
        props.onSuccess(d.order_number, email);
        return;
      }
      attempts += 1;
      await new Promise(function(res) { setTimeout(res, 1500); });
    }
    props.onError('Payment is still processing. Check your email shortly.');
  }

  return React.createElement('div', { 'data-testid': 'stripe-inline-section', className: 'space-y-3' },
    React.createElement(ExpressCheckoutElement, {
      // onClick fires when the customer taps Apple Pay / Google Pay BEFORE the
      // wallet sheet opens. We use it as the safety gate: if the form isn't
      // valid, reject() and the sheet never opens (so they cannot pay an
      // incomplete amount). This + the server-side total recompute together
      // make the early-mount undercharge bug impossible.
      onClick: function(event) {
        var err = props.validate ? props.validate() : null;
        if (err) {
          props.onError(err);
          event.reject();
          return;
        }
        event.resolve({
          emailRequired: false,
          phoneNumberRequired: false,
          shippingAddressRequired: false,
        });
      },
      onConfirm: onExpressConfirm,
      options: { paymentMethods: { applePay: 'always', googlePay: 'always', link: 'auto', paypal: 'never' } }
    }),
    React.createElement('div', { className: 'flex items-center my-3 text-neutral-500 text-xs' },
      React.createElement('div', { className: 'flex-1 h-px bg-white/10' }),
      React.createElement('span', { className: 'px-3 uppercase tracking-wider' }, 'or pay with card'),
      React.createElement('div', { className: 'flex-1 h-px bg-white/10' })
    ),
    React.createElement(PaymentElement, {
      options: {
        layout: 'tabs',
        paymentMethodOrder: ['card'],
        // Hide Apple/Google Pay from this card-form since they're already in Express above
        wallets: { applePay: 'never', googlePay: 'never' }
      }
    }),
    React.createElement('button', {
      type: 'button',
      onClick: onCardPay,
      disabled: processing || !stripe || !elements,
      'data-testid': 'stripe-card-pay-btn',
      className: 'w-full flex items-center justify-center gap-2 bg-white text-black font-semibold py-3 rounded-full hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
    },
      processing ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Lock, { className: 'w-4 h-4' }),
      processing ? 'Processing…' : 'Pay with card'
    ),
    msg ? React.createElement('p', { className: 'text-red-300 text-sm mt-2' }, msg) : null,
    React.createElement('p', { className: 'text-neutral-600 text-[11px] text-center' }, 'Powered by Stripe · PCI compliant · 3D Secure')
  );
}

// PayPal SDK options must be a STABLE reference. If we recreate the object on
// every render, PayPalScriptProvider thinks the options changed and reloads
// the SDK from scratch — which destroys any mounted PayPalButtons mid-flight
// ("zoid destroyed all components"). Defining outside the component guarantees
// the reference never changes.
var PAYPAL_INITIAL_OPTIONS = {
  'client-id': PAYPAL_CLIENT_ID || 'sb',
  currency: 'USD',
  intent: 'capture',
  components: 'buttons',
  'enable-funding': 'paylater,venmo,card',
};

// Public wrapper: provides PayPalScriptProvider context to the inner CheckoutPage
function CheckoutPageWithPayPal() {
  return React.createElement(PayPalScriptProvider, { options: PAYPAL_INITIAL_OPTIONS },
    React.createElement(CheckoutPage)
  );
}

export default CheckoutPageWithPayPal;
